import {
  AbilityId,
  Abilities,
  Actor,
  AiController,
  Bracing,
  Buff,
  Casting,
  Dashing,
  Falling,
  Hazard,
  Instability,
  Pickup,
  PlayerTag,
  Stunned,
  Transform,
  Velocity,
  VortexField,
  type Difficulty,
} from '@/components'
import type { Entity } from '@/core/ecs/Entity'
import type { SystemContext } from '@/core/ecs/Scheduler'
import type { World } from '@/core/ecs/World'
import { clamp01 } from '@/core/math/mathUtils'
import { PUSH, WAVE } from '@/game/abilities/definitions'
import { ArenaResource } from '@/game/arenas/Arena'
import { MatchResource } from '@/game/resources'
import { kindOf, SIGNATURE_USE, signatureWanted } from '@/game/abilities/signatures'
import { InputResource, type PlayerInput } from '@/input/InputManager'

/**
 * Tuning per difficulty. The levels differ in *what the bot notices*, not in
 * what it is allowed to do — a hard bot has no extra abilities and no extra
 * speed, it simply reacts sooner, aims straighter, respects the edge and reads
 * the opponent's wind-ups. That keeps a loss feeling like a loss to a player
 * rather than to a handicap.
 */
interface Profile {
  label: string
  /** Seconds between re-plans. */
  think: number
  /** Radians of aim error applied to movement and facing. */
  aimError: number
  /** How far from the edge the bot starts worrying, in metres. */
  edgeCaution: number
  /** Probability of using a non-basic ability when it would be appropriate. */
  abilityUse: number
  /** Probability of bracing when it sees a wind-up aimed at it. */
  reactBrace: number
  /** Probability of dashing away from a hazard or toward a finisher. */
  useDash: number
  /** Bothers to circle to the inside of the opponent before pushing. */
  playsAngles: boolean
  /** Goes out of its way for pickups. */
  wantsPickups: boolean
  /** Minimum gap between deliberate ability presses. */
  actionGap: number
  /** Chance per decision of simply doing nothing for a beat. */
  hesitate: number
  /** Fraction of top speed the bot actually uses. */
  pace: number
}

export const AI_PROFILES: Record<Difficulty, Profile> = {
  // Genuinely a beginner: slow to notice, wildly inaccurate, dawdles, hesitates,
  // rarely does anything but walk at you and shove. An earlier version of this
  // level was described by a playtester as "hard", which for the easiest of
  // three settings means it was not doing its job.
  0: {
    label: 'НОВИЧОК',
    think: 0.55,
    aimError: 0.75,
    edgeCaution: 1.6,
    abilityUse: 0.08,
    reactBrace: 0,
    useDash: 0.05,
    playsAngles: false,
    wantsPickups: false,
    actionGap: 1.6,
    hesitate: 0.35,
    pace: 0.55,
  },
  1: {
    label: 'СТАЛКЕР',
    think: 0.2,
    aimError: 0.22,
    edgeCaution: 3.4,
    abilityUse: 0.5,
    reactBrace: 0.3,
    useDash: 0.4,
    playsAngles: true,
    wantsPickups: true,
    actionGap: 0.45,
    hesitate: 0.08,
    pace: 0.85,
  },
  2: {
    label: 'МАСТЕР',
    think: 0.08,
    aimError: 0.05,
    // Not the most cautious of the three. At 5 m the master bot hugged the
    // middle and refused to commit, which reads as passivity rather than skill;
    // its edge is reaction and positioning, not turtling.
    edgeCaution: 3.8,
    abilityUse: 0.85,
    reactBrace: 0.8,
    useDash: 0.8,
    playsAngles: true,
    wantsPickups: true,
    actionGap: 0.22,
    hesitate: 0,
    pace: 1,
  },
}

/**
 * A small behaviour-priority AI: survive, then avoid, then take, then fight.
 *
 * There is no pathfinding and no search. On an open platform the only spatial
 * facts that matter are "where is the edge", "where is the danger" and "which
 * side of my opponent am I on", and those are three vector operations.
 */
export function aiSystem({ world, dt }: SystemContext): void {
  const frame = world.tryResource(InputResource)
  const match = world.resource(MatchResource)
  if (!frame) return

  for (const e of world.query([AiController, PlayerTag, Transform, Actor]).entities()) {
    const tag = world.read(e, PlayerTag)
    const input = frame.players[tag.player]
    // A slot with no input frame simply does not act, rather than throwing.
    if (!input) continue
    const ai = world.read(e, AiController)

    clearButtons(input)

    if (match.phase !== 'fight' || world.has(e, Falling) || world.has(e, Stunned)) {
      input.moveX = 0
      input.moveZ = 0
      input.moveMag = 0
      continue
    }

    ai.decisionTimer -= dt
    ai.actionTimer -= dt
    ai.braceTimer -= dt
    const profile = AI_PROFILES[ai.difficulty]

    if (ai.decisionTimer <= 0) {
      ai.decisionTimer = profile.think
      ai.aimError = ai.rng.spread(profile.aimError)
      // Weak bots stall now and then. Standing still for a beat is the most
      // human-looking mistake there is, and it gives the player openings.
      if (profile.hesitate > 0 && ai.rng.bool(profile.hesitate)) {
        ai.moveX = 0
        ai.moveZ = 0
      } else {
        decide(world, e, ai, profile, input)
      }
    }

    // Re-assert the hold on every frame it is still wanted.
    if (ai.braceTimer > 0) {
      input.brace.down = true
      input.moveX = 0
      input.moveZ = 0
      input.moveMag = 0
      continue
    }

    // Movement intent persists between decisions; the bot keeps walking where
    // it last decided to walk instead of stuttering to a halt.
    // Pace scales the analogue magnitude, exactly as a player easing off the
    // stick would: a novice ambles rather than sprinting everywhere.
    input.moveX = ai.moveX * profile.pace
    input.moveZ = ai.moveZ * profile.pace
    input.moveMag = Math.min(1, Math.hypot(input.moveX, input.moveZ))
  }
}

function clearButtons(input: PlayerInput): void {
  // Brace included. It is a hold, but `decide()` only runs on the decision
  // timer, so leaving `down` set meant the bot latched the stance forever —
  // invisible in the browser (the device poll overwrites it every frame) and
  // very visible in the headless harness, which has no device.
  for (const key of ['push', 'dash', 'wave', 'brace', 'vortex'] as const) {
    input[key].pressed = false
    input[key].released = false
    input[key].down = false
  }
}

function decide(
  world: World,
  self: Entity,
  ai: ReturnType<typeof AiController.create>,
  profile: Profile,
  input: PlayerInput,
): void {
  const arena = world.resource(ArenaResource)
  const rng = ai.rng
  const tr = world.read(self, Transform)
  const vel = world.read(self, Velocity)

  const opponent = findOpponent(world, self)
  const toCentreX = arena.def.centerX - tr.x
  const toCentreZ = arena.def.centerZ - tr.z
  const centreLen = Math.hypot(toCentreX, toCentreZ) || 1

  // ── 1. Survive. ──
  //
  // Edge caution is a *weight*, not a switch. As an override it made the good
  // bots refuse to fight: both sides shoved each other outward, both spent the
  // round walking back to the middle, and rounds ran a full minute until the
  // platform collapsed. Now the bot leans inward while still doing its job, and
  // only abandons everything when the ground is genuinely about to run out.
  const edge = -arena.distance(tr.x, tr.z)
  const homeX = toCentreX / centreLen
  const homeZ = toCentreZ / centreLen
  const drift = vel.x * -homeX + vel.z * -homeZ
  const critical = edge < 2 || drift > 5.5

  if (critical) {
    setMove(ai, homeX, homeZ, ai.aimError)
    // A dash is the only thing that beats a hard knockback; spend it.
    if (
      canUse(world, self, AbilityId.Dash) &&
      ai.actionTimer <= 0 &&
      rng.bool(profile.useDash)
    ) {
      input.dash.pressed = true
      input.dash.down = true
      ai.actionTimer = profile.actionGap
    }
    return
  }

  // 0 in the safe middle, approaching 1 at the rim.
  const caution = clamp01(1 - edge / Math.max(profile.edgeCaution, 0.001))

  // ── 2. Avoid whatever is about to go off nearby. ──
  //
  // Blended with the pull toward the middle, exactly like the fight vector is.
  // As a pure "run directly away" it was a herding exploit: put a tornado
  // between the bot and the centre and it walked itself off the platform — and
  // because the rule was gated on caution, the *harder* bots were the ones you
  // could herd.
  const threat = nearestThreat(world, tr.x, tr.z)
  if (threat && profile.edgeCaution > 2.5) {
    setMove(
      ai,
      threat.awayX * (1 - caution) + homeX * caution * 1.6,
      threat.awayZ * (1 - caution) + homeZ * caution * 1.6,
      ai.aimError,
    )
    return
  }

  // ── 3. Take a pickup if it is genuinely on the way. ──
  if (profile.wantsPickups) {
    const pickup = nearestPickup(world, tr.x, tr.z)
    if (pickup && pickup.dist < 9) {
      setMove(ai, pickup.dirX, pickup.dirZ, ai.aimError)
      return
    }
  }

  if (opponent === null) {
    setMove(ai, toCentreX / centreLen, toCentreZ / centreLen, 0)
    return
  }

  // ── 4. Fight. ──
  const ot = world.read(opponent, Transform)
  const dx = ot.x - tr.x
  const dz = ot.z - tr.z
  const dist = Math.hypot(dx, dz) || 1
  const dirX = dx / dist
  const dirZ = dz / dist

  // Defend against a telegraphed wind-up rather than eating it.
  //
  // A dash has zero wind-up and resolves on the step it is requested, so it is
  // never observable as a `Casting`. Watching for the dash itself is the only
  // way this reaction ever fires against the tool a human uses most.
  const incoming =
    world.has(opponent, Casting) ||
    (world.has(opponent, Dashing) && dist < 6) ||
    vortexNearby(world, tr.x, tr.z)

  if (
    incoming &&
    dist < 6 &&
    canUse(world, self, AbilityId.Brace) &&
    rng.bool(profile.reactBrace)
  ) {
    // Bounded on purpose: a stance held indefinitely is a bot standing still.
    ai.braceTimer = 0.85
    input.brace.down = true
    setMove(ai, 0, 0, 0)
    return
  }

  // Where the opponent would fall if pushed straight away from the centre.
  const outX = (ot.x - arena.def.centerX) / (Math.hypot(ot.x - arena.def.centerX, ot.z - arena.def.centerZ) || 1)
  const outZ = (ot.z - arena.def.centerZ) / (Math.hypot(ot.x - arena.def.centerX, ot.z - arena.def.centerZ) || 1)
  const opponentEdge = -arena.distance(ot.x, ot.z)

  let goX = dirX
  let goZ = dirZ
  if (profile.playsAngles) {
    // Approach from the inside, so a landed push sends them outward instead of
    // across the arena. This one line is most of what separates the hard bot.
    const standX = ot.x - outX * (PUSH.range * 0.8)
    const standZ = ot.z - outZ * (PUSH.range * 0.8)
    const sx = standX - tr.x
    const sz = standZ - tr.z
    const slen = Math.hypot(sx, sz) || 1
    if (slen > 0.6) {
      goX = sx / slen
      goZ = sz / slen
    }
  }
  // Going for the kill: once the opponent is badly rattled, one clean hit ends
  // it, and the bot accepts more risk to land it. Without this, two careful
  // bots simply orbit each other until the platform collapses.
  const opponentInstability = world.get(opponent, Instability)?.value ?? 0
  const aggression = 1 - 0.55 * clamp01(opponentInstability / 100)

  const weight = caution * aggression
  const blendX = goX * (1 - weight) + homeX * weight * 1.4
  const blendZ = goZ * (1 - weight) + homeZ * weight * 1.4
  setMove(ai, blendX, blendZ, ai.aimError)

  if (ai.actionTimer > 0) return

  const inCone = dist < PUSH.range + 0.5
  const instability = opponentInstability
  // Against a shielded opponent the bot used to stop attacking entirely for the
  // full duration — a free round for anyone who grabbed one. It now backs off
  // instead: nothing it throws will land, but it keeps its distance and its
  // charges, and re-engages the moment the bubble drops.
  if ((world.get(opponent, Buff)?.shield ?? 0) > 0) {
    setMove(ai, -dirX * 0.8 + homeX * 0.6, -dirZ * 0.8 + homeZ * 0.6, ai.aimError)
    return
  }

  // Push whenever it can actually connect: it is the cheapest thing available.
  if (inCone && canUse(world, self, AbilityId.Push)) {
    input.push.pressed = true
    input.push.down = true
    ai.actionTimer = profile.actionGap
    return
  }

  // The Q slot. What sits there is personal, so the window comes from the same
  // table the move does — see SIGNATURE_USE.
  const kind = kindOf(world, self)
  const situation = {
    dist,
    selfEdge: edge,
    opponentEdge,
    opponentInstability: instability,
  }
  const qUse = kind
    ? SIGNATURE_USE[kind]?.[AbilityId.Wave]
    : { maxDist: WAVE.maxRadius * 0.75, finisher: true }

  if (
    signatureWanted(qUse, situation) &&
    canUse(world, self, AbilityId.Wave) &&
    rng.bool(profile.abilityUse)
  ) {
    input.wave.pressed = true
    input.wave.down = true
    ai.actionTimer = profile.actionGap
    return
  }

  // Close the gap with a dash when the opponent is near their edge.
  if (
    dist > PUSH.range &&
    dist < 8 &&
    opponentEdge < 7 &&
    canUse(world, self, AbilityId.Dash) &&
    rng.bool(profile.useDash)
  ) {
    input.dash.pressed = true
    input.dash.down = true
    ai.actionTimer = profile.actionGap
    return
  }

  // The R slot. Same table; the one extra condition is that a move which
  // commits the caster is only taken with an escape still in hand — and a panic
  // move is exempt, because needing the escape is the reason it is being cast.
  const rUse = kind
    ? SIGNATURE_USE[kind]?.[AbilityId.Vortex]
    : { maxDist: 4.5, needsRoom: true }

  if (
    signatureWanted(rUse, situation) &&
    (rUse?.panic || hasDashCharge(world, self)) &&
    canUse(world, self, AbilityId.Vortex) &&
    rng.bool(profile.abilityUse)
  ) {
    input.vortex.pressed = true
    input.vortex.down = true
    ai.actionTimer = profile.actionGap * 2
  }
}

function setMove(
  ai: ReturnType<typeof AiController.create>,
  x: number,
  z: number,
  error: number,
): void {
  const len = Math.hypot(x, z)
  if (len < 1e-4) {
    ai.moveX = 0
    ai.moveZ = 0
    return
  }
  const angle = Math.atan2(x / len, z / len) + error
  ai.moveX = Math.sin(angle)
  ai.moveZ = Math.cos(angle)
}

/**
 * The nearest fighter still standing.
 *
 * In a free-for-all "the opponent" is whoever is closest — going after a
 * distant target across a crowded platform makes a bot look lost, and the
 * fighter in your face is the one about to push you off.
 */
function findOpponent(world: World, self: Entity): Entity | null {
  const tr = world.get(self, Transform)
  if (!tr) return null

  const match = world.tryResource(MatchResource)
  const myTeam = teamOf(world, match, self)

  let best: Entity | null = null
  let bestDist = Infinity
  for (const e of world.query([PlayerTag, Transform]).entities()) {
    if (e === self || world.has(e, Falling)) continue
    // A bot that charges its own partner would spend the round shoulder-barging
    // someone it cannot even move.
    if (myTeam !== null && teamOf(world, match, e) === myTeam) continue
    const other = world.read(e, Transform)
    const dist = Math.hypot(other.x - tr.x, other.z - tr.z)
    if (dist < bestDist) {
      bestDist = dist
      best = e
    }
  }
  return best
}

/** Team of a fighter, or null when the world has no match state (tests). */
function teamOf(
  world: World,
  match: { teams: number[] } | undefined,
  e: Entity,
): number | null {
  if (!match) return null
  const tag = world.get(e, PlayerTag)
  if (!tag) return null
  return match.teams[tag.player] ?? tag.player
}

function canUse(world: World, e: Entity, ability: number): boolean {
  if (world.has(e, Casting) || world.has(e, Dashing) || world.has(e, Bracing)) return false
  const abilities = world.get(e, Abilities)
  return abilities !== undefined && abilities.charges[ability] > 0
}

function hasDashCharge(world: World, e: Entity): boolean {
  const abilities = world.get(e, Abilities)
  return abilities !== undefined && abilities.charges[AbilityId.Dash] > 0
}

/** The closest hazard worth stepping out of, with the direction to step. */
function nearestThreat(
  world: World,
  x: number,
  z: number,
): { awayX: number; awayZ: number } | null {
  let best: { awayX: number; awayZ: number; score: number } | null = null

  for (const e of world.query([Hazard, Transform]).entities()) {
    const hazard = world.read(e, Hazard)
    const tr = world.read(e, Transform)
    const dx = x - tr.x
    const dz = z - tr.z
    const dist = Math.hypot(dx, dz) || 1
    if (dist > hazard.radius) continue

    // A burner about to fire is urgent; a vortex is a constant, milder problem.
    const imminence =
      hazard.interval > 0 ? Math.pow(hazard.timer / Math.max(hazard.interval, 0.001), 2) : 0.5
    const score = (1 - dist / hazard.radius) * (0.4 + imminence)
    if (!best || score > best.score) {
      best = { awayX: dx / dist, awayZ: dz / dist, score }
    }
  }
  return best && best.score > 0.35 ? best : null
}

/** A player-placed vortex within its pull radius is worth reacting to. */
function vortexNearby(world: World, x: number, z: number): boolean {
  for (const e of world.query([VortexField, Transform]).entities()) {
    const field = world.read(e, VortexField)
    if (field.phase === 'burst') continue
    const tr = world.read(e, Transform)
    if (Math.hypot(tr.x - x, tr.z - z) < field.radius) return true
  }
  return false
}

function nearestPickup(
  world: World,
  x: number,
  z: number,
): { dirX: number; dirZ: number; dist: number } | null {
  let best: { dirX: number; dirZ: number; dist: number } | null = null
  for (const e of world.query([Pickup, Transform]).entities()) {
    const tr = world.read(e, Transform)
    const dx = tr.x - x
    const dz = tr.z - z
    const dist = Math.hypot(dx, dz) || 1
    if (!best || dist < best.dist) best = { dirX: dx / dist, dirZ: dz / dist, dist }
  }
  return best
}
